// src/com/veh_info/VehInfoTransPosConfig.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Button, DialogActions,
    IconButton,
    CircularProgress,
    Tooltip
} from '@mui/material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt'; // header icon.
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import CheckBoxIcon from '@mui/icons-material/CheckBox'; // It's now Y. Click to toggle.
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'; // It's now N. Click to toggle.
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'; // 일괄 설정.
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
// import { useSnackbar } from 'notistack';
import { AppWord, AppStyle, AppObject, TableColumnType } from '../../value/AppConst';
import DraggableEditor from '../../common/DraggableEditor';
import { authTableTreeRepo } from '../../value/AppModel';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import {
    LIST_VEH_INFO, SET_VEH_INFO_TRANS_POS_YN,
    SET_VEH_INFO_MULTIPLE_TRANS_POS_YN
} from './VehInfoGql';
import { VehInfoDictionary } from './VehInfoDict';
import SearchBox from '../../common/SearchBox';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { useConfirmDialog } from '../../common/AppConfirmDialog';

const ContentBox = styled(Box)({
    maxHeight: '75vh', minHeight:'60vh',
    display: 'flex', flexDirection: 'column', 
});

const ControlBox = styled(Box)({display:'flex', alignItems:'center', paddingBottom: 10});

export default function VehInfoTransPosConfig({
    open,
    onClose
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [vehInfoList, setVehInfoList] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [workingVehicle, setWorkingVehicle] = useState({});
    const openConfirmDialog = useConfirmDialog();
    // const { enqueueSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    // const gotRef = useRef(false);

    // session 정보에 따라서 운송사 혹은 센터 소속 차량 목록을 가져온다.
    const [getVehInfoList, responseVehInfoList] = useLazyQuery(LIST_VEH_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [setVehInfoTransPosYn, responseSetTransPosYn] = useMutation( SET_VEH_INFO_TRANS_POS_YN, {
		onCompleted: (data, option) => onCompleteSetTransPosYn(data, option),
		onError: (error) => {
            setWorkingVehicle({});
            addAlert({title: '관제설정 오류', message: [error.message]});
        }
	} );
    const [setVehInfoMultipleTransPosYn, responseSetMultipleTransPosYn] = useMutation( SET_VEH_INFO_MULTIPLE_TRANS_POS_YN, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => onCompleteSetMultipleTransPosYn(data, option),
        onError: (error) => addAlert({title: '일괄 관제설정 오류', message: [error.message]})
    });

    const canSet = checkAuth(authTableTree.tree.pr_veh_info_set_trans_pos_yn);

    useEffect(()=>{
        if(open) {
            getVehInfoList({variables:{rentYn:'N'}}); // 세션에 따라 운송사 혹은 센터의 차량 목록을 가져온다.
            // gotRef.current = true;
        }
    }, [open, getVehInfoList])

    // 운송사의 것이든, 센터의 것이든 모두 처리.
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoList) {
            setVehInfoList(data.vehInfoList);
        }
    };

    const onCompleteSetTransPosYn = (data, clientOption) => {
        setWorkingVehicle({});
        if(data.vehInfoSetTransPosYn.ok) {
            setVehInfoList(data.vehInfoSetTransPosYn.list);
        }
        else addAlert({title:'관제설정 오류', message:[data.vehInfoSetTransPosYn.message]});
    };

    const onCompleteSetMultipleTransPosYn = (data, clientOption) => {
        if(data.vehInfoSetMultipleTransPosYn.ok) {
            setVehInfoList(data.vehInfoSetMultipleTransPosYn.list);
        }
        else addAlert({title:'일괄 관제설정 오류', message:[data.vehInfoSetMultipleTransPosYn.message]});
    };

    const setMultipleTransPosYn = () => {
        const vehInfoSeqList = vehInfoList.filter(veh=>veh.transPosYn!=='Y').map(veh=>veh.vehInfoSeq);
        setVehInfoMultipleTransPosYn({variables:{vehInfo:{vehInfoSeqList, transPosYn:'Y'}}});
    };

    const confirmSetMultipleTransPosYn = async () => {
        const confirm = await openConfirmDialog({
            title: '일괄 관제설정',
            content: [
                '관제설정이 되어 있지 않은 모든 차량에 대해 일괄적으로 설정합니다.',
                '차량별로 확인이 필요한 경우 개별 설정을 하십시오.',
                '계속하시겠습니까?'
            ]
        });

        if(confirm) setMultipleTransPosYn();
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickToggleTransPosYn = (item) => {
        setWorkingVehicle(item);
        setVehInfoTransPosYn({
            variables:{vehInfo:{vehInfoSeq:item.vehInfoSeq,
                transPosYn:item.transPosYn==='Y'?'N':'Y'
            }}});
    };

    const confTabpeProps = {
        tableId: 'vehInfoTransPosConfigTable',
        uniqueKey: 'vehInfoSeq',
        flexOption: {
            multiChecker:false,
            hideFirstTopButton: false,
            hideColumnConfig: true,
            hideBubble: true,
            growToFill: true,
        },
        columns: [
            { id: 'comName', label: '운송사', width: 130, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehPlates', label: '차량번호', width: 100, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverName', label: '운전자', width: 80, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverMobile', label: '전화', width: 120, show: true, privacy:true, option:{align: 'center', type:TableColumnType.TEXT} },
            {
                id: 'transPosYn', label: '표시여부', width: 60, show: true,
                option: {
                    align: 'center',
                    print: (r, i, v) => {
                        if(responseSetMultipleTransPosYn.loading)
                            return <CircularProgress size={18} />;

                        return(
                            <IconButton
                                size={AppWord.SMALL}
                                color={AppStyle.mui.warning}
                                onClick={()=>onClickToggleTransPosYn(r)}
                                disabled={!canSet || (responseSetTransPosYn.loading && workingVehicle.vehInfoSeq===r.vehInfoSeq)}
                            >
                                {v==='Y' ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                            </IconButton>
                        );
                    }
                }
            }
        ]
    };

    const tableWidth = confTabpeProps.columns.reduce((acc, col)=>acc+col.width, 80);

    const loadingAny
    = responseVehInfoList.loading
    || responseSetTransPosYn.loading
    || responseSetMultipleTransPosYn.loading;

    return (
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClose}
            startIcon={<AddLocationAltIcon size={AppWord.LARGE}/>}
            title="관제차량 선택"
        >
            <DialogContent>
                <ContentBox minWidth={tableWidth}>
                    <ControlBox>
                        <SearchBox
                            value={searchText}
                            onChangeText={onChangeSearchText}
                            disabled={loadingAny}
                        />
                    </ControlBox>
                    <FlexyTable
                        name={confTabpeProps.tableId}
                        tableProps={confTabpeProps}
                        records={vehInfoList}
                        search={searchText}
                        tools={[]}
                    />
                </ContentBox>
            </DialogContent>
            <DialogActions>
                <Tooltip title="관제설정 되어 있지 않은 차량에 대해 일괄적으로 설정">
                <Button
                    onClick={confirmSetMultipleTransPosYn}
                    color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    disabled={loadingAny || !canSet}
                    startIcon={<ChecklistRtlIcon />}
                >
                    관제설정 되어 있지 않은 차량 일괄 설정
                </Button>
                </Tooltip>
                <Button onClick={onClose} color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    disabled={loadingAny}
                    startIcon={<DoneIcon />}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}